import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { ChevronLeft, Delete } from 'lucide-react';
import HUDFrame from './ui/HUDFrame';
import ArmyLogo from './united-states-army-2023-seeklogo.png';

interface NameEntryProps {
  onSubmit: (lastName: string) => void;
  onBack?: () => void;
  initialLastName?: string;
}

// Keyboard layout
const KEYBOARD_ROWS = [
  ['Q', 'W', 'E', 'R', 'T', 'Y', 'U', 'I', 'O', 'P'],
  ['A', 'S', 'D', 'F', 'G', 'H', 'J', 'K', 'L'],
  ['Z', 'X', 'C', 'V', 'B', 'N', 'M'],
];

const NameEntry: React.FC<NameEntryProps> = ({ onSubmit, onBack, initialLastName }) => {
  const [lastName, setLastName] = useState(initialLastName || '');

  const handleSubmit = () => {
    if (lastName.trim()) {
      onSubmit(lastName.trim().toUpperCase());
    }
  };

  const handleVirtualKeyPress = (key: string) => {
    if (lastName.length < 20) {
      setLastName(prev => prev + key);
    }
  };

  const handleBackspace = () => {
    setLastName(prev => prev.slice(0, -1));
  };

  const handleClear = () => {
    setLastName('');
  };

  return (
    <motion.div
      className="h-full w-full flex items-center justify-center bg-army-black tactical-grid"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.2 }}
    >
      <HUDFrame className="w-full max-w-5xl 4k:max-w-[180rem] mx-auto p-6 4k:p-24">
        <div className="text-center space-y-4 4k:space-y-16">
          {/* Back Button */}
          {onBack && (
            <motion.button
              onClick={onBack}
              className="absolute left-6 top-20 4k:top-40 flex items-center space-x-2 4k:space-x-4 text-army-gold hover:text-white transition-colors group z-10"
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.1 }}
              whileHover={{ x: -5 }}
            >
              <ChevronLeft className="w-6 h-6 4k:w-16 4k:h-16" />
              <span className="text-sm 4k:text-3xl uppercase tracking-wider font-bold">Back</span>
            </motion.button>
          )}

          {/* Army Logo */}
          <motion.div
            initial={{ y: -15, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ duration: 0.3 }}
          >
            <img src={ArmyLogo} alt="U.S. Army" className="h-20 4k:h-64 w-auto mx-auto" />
          </motion.div>

          {/* Header */}
          <motion.div
            initial={{ y: -15, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.05, duration: 0.3 }}
          >
            <h2 className="text-2xl 4k:text-8xl text-army-gold uppercase tracking-widest mb-2 4k:mb-12">
              Personnel Identification
            </h2>
            <h1 className="text-5xl 4k:text-11xl font-bold text-white 4k:leading-none">
              ENTER YOUR LAST NAME
            </h1>
          </motion.div>

          {/* Progress indicator */}
          <motion.div
            className="flex items-center justify-center space-x-2 4k:space-x-8"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.1 }}
          >
            <div className="w-12 4k:w-48 h-1 4k:h-4 bg-army-gold" />
            <div className="w-12 4k:w-48 h-1 4k:h-4 bg-gray-600" />
            <div className="w-12 4k:w-48 h-1 4k:h-4 bg-gray-600" />
            <div className="w-12 4k:w-48 h-1 4k:h-4 bg-gray-600" />
            <div className="w-12 4k:w-48 h-1 4k:h-4 bg-gray-600" />
          </motion.div>

          {/* Instructions */}
          <motion.p
            className="text-xl 4k:text-7xl text-gray-400"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.15 }}
          >
            Your last name will appear on your uniform name tape
          </motion.p>

          {/* Name Display */}
          <motion.div
            className="max-w-3xl 4k:max-w-[100rem] mx-auto"
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.2, duration: 0.3 }}
          >
            <div className="relative">
              <div className="w-full bg-army-dark border-4 4k:border-12 border-army-gold px-8 4k:px-16 py-4 4k:py-12 min-h-[4rem] 4k:min-h-[12rem] flex items-center justify-center">
                <span className="text-4xl 4k:text-[6rem] font-bold text-white uppercase tracking-wider">
                  {lastName || <span className="text-gray-600">LAST NAME</span>}
                </span>
              </div>
              {/* Corner accents */}
              <div className="absolute top-0 left-0 w-6 h-6 4k:w-20 4k:h-20 border-t-4 border-l-4 4k:border-t-12 4k:border-l-12 border-army-gold pointer-events-none" />
              <div className="absolute top-0 right-0 w-6 h-6 4k:w-20 4k:h-20 border-t-4 border-r-4 4k:border-t-12 4k:border-r-12 border-army-gold pointer-events-none" />
              <div className="absolute bottom-0 left-0 w-6 h-6 4k:w-20 4k:h-20 border-b-4 border-l-4 4k:border-b-12 4k:border-l-12 border-army-gold pointer-events-none" />
              <div className="absolute bottom-0 right-0 w-6 h-6 4k:w-20 4k:h-20 border-b-4 border-r-4 4k:border-b-12 4k:border-r-12 border-army-gold pointer-events-none" />
            </div>
          </motion.div>

          {/* On-Screen Keyboard */}
          <motion.div
            className="max-w-4xl 4k:max-w-[120rem] mx-auto space-y-2 4k:space-y-6"
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.25, duration: 0.3 }}
          >
            {KEYBOARD_ROWS.map((row, rowIndex) => (
              <div key={rowIndex} className="flex justify-center gap-1.5 4k:gap-4">
                {row.map((key) => (
                  <motion.button
                    key={key}
                    onClick={() => handleVirtualKeyPress(key)}
                    className="w-12 h-12 4k:w-32 4k:h-32 bg-army-dark border-2 4k:border-4 border-army-gold/70 hover:border-army-gold hover:bg-army-gold/20 text-white font-bold text-xl 4k:text-5xl uppercase transition-all"
                    whileHover={{ scale: 1.1 }}
                    whileTap={{ scale: 0.95, backgroundColor: 'rgba(212, 175, 55, 0.4)' }}
                  >
                    {key}
                  </motion.button>
                ))}
              </div>
            ))}

            {/* Bottom row with special keys */}
            <div className="flex justify-center gap-1.5 4k:gap-4">
              <motion.button
                onClick={handleClear}
                className="px-4 4k:px-12 h-12 4k:h-32 bg-army-dark border-2 4k:border-4 border-red-500/70 hover:border-red-500 hover:bg-red-500/20 text-red-500 font-bold text-sm 4k:text-3xl uppercase transition-all"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                Clear
              </motion.button>
              <motion.button
                onClick={() => handleVirtualKeyPress('-')}
                className="w-16 4k:w-40 h-12 4k:h-32 bg-army-dark border-2 4k:border-4 border-army-gold/70 hover:border-army-gold hover:bg-army-gold/20 text-white font-bold text-2xl 4k:text-6xl transition-all"
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.95, backgroundColor: 'rgba(212, 175, 55, 0.4)' }}
              >
                -
              </motion.button>
              <motion.button
                onClick={() => handleVirtualKeyPress(' ')}
                className="w-32 4k:w-80 h-12 4k:h-32 bg-army-dark border-2 4k:border-4 border-army-gold/70 hover:border-army-gold hover:bg-army-gold/20 text-white font-bold text-sm 4k:text-3xl uppercase transition-all"
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98, backgroundColor: 'rgba(212, 175, 55, 0.4)' }}
              >
                Space
              </motion.button>
              <motion.button
                onClick={handleBackspace}
                className="px-4 4k:px-12 h-12 4k:h-32 bg-army-dark border-2 4k:border-4 border-army-gold/70 hover:border-army-gold hover:bg-army-gold/20 text-army-gold font-bold text-sm 4k:text-3xl uppercase transition-all flex items-center gap-2 4k:gap-4"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                <Delete className="w-5 h-5 4k:w-10 4k:h-10" />
                <span>Delete</span>
              </motion.button>
            </div>
          </motion.div>

          {/* Continue Button */}
          <motion.button
            onClick={handleSubmit}
            disabled={!lastName.trim()}
            className="group relative"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: lastName.trim() ? 1 : 0.5, y: 0 }}
            transition={{ delay: 0.3, duration: 0.3 }}
            whileHover={lastName.trim() ? { scale: 1.05 } : {}}
            whileTap={lastName.trim() ? { scale: 0.95 } : {}}
          >
            <div className={`relative bg-gradient-to-b from-army-dark to-army-black border-4 4k:border-12 border-army-gold px-16 4k:px-48 py-4 4k:py-16 ${lastName.trim() ? 'card-hover' : 'opacity-50'}`}>
              <span className="text-2xl 4k:text-7xl font-bold text-white uppercase tracking-wider">
                Continue
              </span>

              {/* Corner accents */}
              <div className="absolute top-0 left-0 w-6 h-6 4k:w-16 4k:h-16 border-t-4 border-l-4 4k:border-t-12 4k:border-l-12 border-army-gold" />
              <div className="absolute top-0 right-0 w-6 h-6 4k:w-16 4k:h-16 border-t-4 border-r-4 4k:border-t-12 4k:border-r-12 border-army-gold" />
              <div className="absolute bottom-0 left-0 w-6 h-6 4k:w-16 4k:h-16 border-b-4 border-l-4 4k:border-b-12 4k:border-l-12 border-army-gold" />
              <div className="absolute bottom-0 right-0 w-6 h-6 4k:w-16 4k:h-16 border-b-4 border-r-4 4k:border-b-12 4k:border-r-12 border-army-gold" />
            </div>
          </motion.button>

          {/* Bottom Status */}
          <motion.div
            className="pt-2 4k:pt-8"
            initial={{ opacity: 0 }}
            animate={{ opacity: 0.6 }}
            transition={{ delay: 0.35 }}
          >
            <p className="text-sm 4k:text-3xl text-gray-500 uppercase tracking-widest">
              Step 1 of 5 • Personnel Identification
            </p>
          </motion.div>
        </div>
      </HUDFrame>
    </motion.div>
  );
};

export default NameEntry;
